import React, { useState, useContext, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    CircularProgress,
    Dialog,
    Slide,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    Chip,
    ListItemText,
    DialogTitle,
    useTheme,
} from "@mui/material";
import { UserContext } from "src/Contexts/UserContext";
import { notify } from "src/utils/notifyFunction";
import axios from "axios";
import { REGISTER_GRIEVANCE, FETCH_MODULE, DASHBOARD_URL, BASIC_TOKEN_HEADER } from "src/constants/api.config";
import useBreakpoints from "../../../utils/Breakpoints";
import { sanitizeInput, isInputSafe } from 'src/utils/sanitizeInput';
import { getUserToken } from "src/utils/handleCookie";

const FooterForm = ({ open, onClose }) => {
    const [userName, setUserName] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [forModule, setForModule] = useState([]);
    const [modules, setModules] = useState([]);
    const [complainDescription, setComplainDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { contextUserData } = useContext(UserContext);
    const theme = useTheme();
    const { isSm } = useBreakpoints();

    const handleReset = () => {
        setUserName("");
        setMobileNumber("");
        setForModule([]);
        setComplainDescription("");
    };

    const modulesFetcher = async () => {
        try {
            const { data } = await axios.get(`${DASHBOARD_URL}/${FETCH_MODULE}`, {
                headers: BASIC_TOKEN_HEADER
            });
            if (data.Links) {
                setModules(data.Links);
            }
        } catch (error) {
            notify("Server Error", "error");
        }
    };

    useEffect(() => {
        modulesFetcher();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Input validation
        if (
            !isInputSafe(userName) ||
            !isInputSafe(mobileNumber) ||
            !isInputSafe(complainDescription)
        ) {
            notify("Invalid input detected!", "error");
            setIsSubmitting(false);
            return;
        }
        // Optionally sanitize
        const safeUserName = sanitizeInput(userName);
        const safeMobileNumber = sanitizeInput(mobileNumber);
        const safeComplainDescription = sanitizeInput(complainDescription);

        const params = new URLSearchParams({
            OperatorUserName: safeUserName,
            MobileNo: safeMobileNumber,
            LoginUserID: getUserToken(),
            OfficerName: contextUserData.UserName,
            ComplainDesc: safeComplainDescription,
            ForModule: forModule.join(","),
            LoginID: getUserToken(),
        });

        const config = {
            method: "GET",
            url: `${DASHBOARD_URL}/${REGISTER_GRIEVANCE}?${params.toString()}`,
            headers: BASIC_TOKEN_HEADER
        };

        try {
            const result = await axios(config);
            const { ErrCode, ErrMsg } = result.data.ErrMsg[0];
            if (ErrCode === "1" && ErrMsg) {
                notify(ErrMsg, "success");
            }
        } catch (error) {
            notify("Server Error", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleModuleChange = (event) => {
        const { target: { value } } = event;
        setForModule(typeof value === "string" ? value.split(",") : value);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            TransitionComponent={Slide}
            transitionDuration={500}
            PaperProps={{
                elevation: 8,
                sx: { borderRadius: isSm ? 0 : 2 },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #eee",
                    p: isSm ? 2 : 3,
                    pb: 1,
                }}
            >
                <Typography
                    variant={isSm ? 'h6' : "h5"}
                    sx={{
                        color: "#1976d2",
                        fontWeight: 600,
                    }}
                >
                    Register Grievance
                </Typography>
            </DialogTitle>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    p: isSm ? 2 : 3,
                }}
            >
                <TextField
                    fullWidth
                    label="Login User ID"
                    variant="filled"
                    value={contextUserData.LoginUserID || ""}
                    InputProps={{
                        readOnly: true,
                    }}
                    sx={{ mb: 2, backgroundColor: "#eff6ff" }}
                    size={isSm ? "small" : "medium"}
                />
                <TextField
                    fullWidth
                    label="Officer Name"
                    variant="filled"
                    value={contextUserData.UserName || ""}
                    InputProps={{
                        readOnly: true,
                    }}
                    sx={{ mb: 2, backgroundColor: "#eff6ff" }}
                    size={isSm ? "small" : "medium"}
                />
                <TextField
                    fullWidth
                    label="User/Operator Name"
                    variant="filled"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    required
                    sx={{ mb: 2 }}
                    size={isSm ? "small" : "medium"}
                />
                <TextField
                    fullWidth
                    label="Mobile Number"
                    variant="filled"
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    required
                    sx={{ mb: 2 }}
                    size={isSm ? "small" : "medium"}
                />
                <FormControl fullWidth variant="filled" sx={{ mb: 2 }} size={isSm ? "small" : "medium"}>
                    <InputLabel id="forModule-label">For Module</InputLabel>
                    <Select
                        labelId="forModule-label"
                        value={forModule}
                        onChange={handleModuleChange}
                        multiple
                        renderValue={(selected) => (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value} label={value} size="small" />
                                ))}
                            </Box>
                        )}
                    >
                        {modules.map((module) => (
                            <MenuItem key={module.CODE} value={module.CODE}>
                                <Checkbox checked={forModule.indexOf(module.CODE) > -1} />
                                <ListItemText primary={module.DESCRIPTION} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    fullWidth
                    label="Complaint Description"
                    variant="filled"
                    value={complainDescription}
                    onChange={(e) => setComplainDescription(e.target.value)}
                    multiline
                    rows={4}
                    required
                    sx={{ mb: 2 }}
                    size={isSm ? "small" : "medium"}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", columnGap: 2 }}>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        sx={{ textTransform: "none" }}
                        size={isSm ? "small" : "medium"}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                            bgcolor: "#1976d2",
                            "&:hover": { bgcolor: "#1565c0" },
                            textTransform: "none",
                        }}
                        size={isSm ? "small" : "medium"}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default FooterForm;
